import "@/styles/globals.css";
import type { AppProps } from "next/app";
import dynamic from "next/dynamic";
import { appWithTranslation } from "next-i18next";
import { useEffect, useState } from "react";

// Fonts - optimized with display swap
import { Inter } from "next/font/google";
// Swiper styles
import "swiper/css";
// Redux - required for state management (but defer persist)
import store from "@/store/store";
import { Provider } from "react-redux";

// React Query - required for data fetching
import { QueryClient, QueryClientProvider } from "react-query";

// Critical layout - load immediately
import Layout from "@/layout";
// Must render on the server: these wrap the whole tree, so deferring them
// leaves the server HTML empty for every page.
import SafeArea from "@/components/safeArea";
import AntdConfig from "@/utils/antdConfig";

// Dynamically import non-critical components
const Globals = dynamic(() => import("@/globals"), { ssr: false });
const TimeDifference = dynamic(() => import("@/components/timeDifference"), {
  ssr: false,
});
const MainHeadWithTitle = dynamic(() => import("@/components/mainHead"), {
  ssr: false,
});

// Toast - defer loading, only needed for interactions
const Toaster = dynamic(
  () => import("react-hot-toast").then((mod) => mod.Toaster),
  { ssr: false }
);

// Create QueryClient ONCE with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

export const inter = Inter({
  subsets: ["latin"],
  variable: "--font-inter",
  display: "swap",
  preload: true,
});

// Wrapper that shows content immediately, then adds persist later
function AppContent({ Component, pageProps }: AppProps) {
  const [persistReady, setPersistReady] = useState(false);

  useEffect(() => {
    // Defer redux-persist setup to not block initial render
    import("@/store/store").then(({ persistor }) => {
      import("redux-persist/integration/react").then(({ PersistGate }) => {
        persistor.persist();
        setPersistReady(true);
      });
    });

    // Defer error handling setup
    import("@/utils/errorHandler").then(({ setupGlobalErrorHandling }) => {
      setupGlobalErrorHandling();
    });
  }, []);

  return (
    <AntdConfig>
      <SafeArea>
        <Toaster position="top-center" />
        <Layout>
          <Component {...pageProps} />
          <TimeDifference />
          <Globals />
        </Layout>
      </SafeArea>
    </AntdConfig>
  );
}

function App(props: AppProps) {
  return (
    <div className={inter.variable}>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <MainHeadWithTitle />
          <AppContent {...props} />
        </Provider>
      </QueryClientProvider>
    </div>
  );
}

export default appWithTranslation(App);
